import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Theme context supports three modes:
 *   • "auto"  — follow the OS prefers-color-scheme media query (default)
 *   • "light" — force light
 *   • "dark"  — force dark
 *
 * `theme` is the effective theme actually applied to the document (light|dark);
 * `mode`  is the user's chosen preference (auto|light|dark) and is what we
 * persist. New users default to "auto" so warehouse iPads automatically
 * darken overnight without anyone touching the toggle.
 */
const ThemeContext = createContext({
  theme: "light",
  mode: "auto",
  setMode: () => {},
  toggle: () => {},
});

const KEY = "cd_theme";
const MODES = ["auto", "light", "dark"];

function readInitialMode() {
  try {
    const saved = localStorage.getItem(KEY);
    if (MODES.includes(saved)) return saved;
  } catch (err) {
    console.warn("Theme read from localStorage failed:", err);
  }
  return "auto";
}

function systemTheme() {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => readInitialMode());
  const [systemPref, setSystemPref] = useState(() => systemTheme());

  // Track OS preference changes so "auto" mode reacts live (e.g., at sunset).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemPref(e.matches ? "dark" : "light");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const theme = mode === "auto" ? systemPref : mode;

  // Apply the resolved theme to <html> and persist the user's mode choice.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem(KEY, mode); } catch (err) { console.warn("Theme persist failed:", err); }
  }, [theme, mode]);

  const value = useMemo(() => ({
    theme,
    mode,
    setMode: (next) => {
      if (MODES.includes(next)) setModeState(next);
    },
    // Backwards-compat toggle: cycle light → dark → auto → light.
    toggle: () => setModeState((m) => {
      if (m === "light") return "dark";
      if (m === "dark") return "auto";
      return "light";
    }),
  }), [theme, mode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
